import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe2 } from 'lucide-react';

const Countries = () => {
  const [activeFilter, setActiveFilter] = useState('A-Z');
  const sectionRefs = useRef({});

  // Region data with user-provided flag map images
  const regions = [
    {
      name: 'Africa',
      description: 'Comprehensive intelligence across 54 nations, from established producers to frontier exploration markets.',
      image: 'https://customer-assets.emergentagent.com/job_3d67b805-10ac-45e8-9159-aeffcf377b81/artifacts/qtoxdtko_Africa.png'
    },
    {
      name: 'Asia Pacific',
      description: 'Strategic analysis spanning major demand centers and emerging energy economies across the Indo-Pacific.',
      image: 'https://customer-assets.emergentagent.com/job_3d67b805-10ac-45e8-9159-aeffcf377b81/artifacts/x8rj57rv_Asia%20Pacific.png'
    },
    {
      name: 'Central and South America',
      description: 'Deep-dive coverage from pre-salt basins to critical minerals and renewable energy development.',
      image: 'https://customer-assets.emergentagent.com/job_3d67b805-10ac-45e8-9159-aeffcf377b81/artifacts/5bqjq2d9_Central%20and%20South%20America.png'
    },
    {
      name: 'Eurasia',
      description: 'Expert analysis of strategic energy corridors, major hydrocarbon producers, and transit dynamics.',
      image: 'https://customer-assets.emergentagent.com/job_3d67b805-10ac-45e8-9159-aeffcf377b81/artifacts/k3zmrhdq_Eurasia.png'
    },
    {
      name: 'Europe',
      description: 'Policy-driven intelligence on energy transition, market liberalization, and regulatory frameworks.',
      image: 'https://customer-assets.emergentagent.com/job_3d67b805-10ac-45e8-9159-aeffcf377b81/artifacts/5znb1n75_Europe.png'
    },
    {
      name: 'Middle East',
      description: 'Authoritative coverage of OPEC+ dynamics, national oil company strategies, and regional developments.',
      image: 'https://customer-assets.emergentagent.com/job_3d67b805-10ac-45e8-9159-aeffcf377b81/artifacts/264oxcnw_Middle%20East.png'
    },
    {
      name: 'North America',
      description: 'In-depth analysis of shale dynamics, LNG exports, infrastructure, and policy developments.',
      image: 'https://customer-assets.emergentagent.com/job_3d67b805-10ac-45e8-9159-aeffcf377b81/artifacts/eh2j0qsj_North%20America.png'
    }
  ];

  // Comprehensive country list with flag codes
  const countriesByLetter = {
    A: [
      { name: 'Afghanistan', code: 'af' },
      { name: 'Albania', code: 'al' },
      { name: 'Algeria', code: 'dz' },
      { name: 'Angola', code: 'ao' },
      { name: 'Argentina', code: 'ar' },
      { name: 'Armenia', code: 'am' },
      { name: 'Australia', code: 'au' },
      { name: 'Austria', code: 'at' },
      { name: 'Azerbaijan', code: 'az' }
    ],
    B: [
      { name: 'Bahrain', code: 'bh' },
      { name: 'Bangladesh', code: 'bd' },
      { name: 'Belarus', code: 'by' },
      { name: 'Belgium', code: 'be' },
      { name: 'Bolivia', code: 'bo' },
      { name: 'Bosnia and Herzegovina', code: 'ba' },
      { name: 'Botswana', code: 'bw' },
      { name: 'Brazil', code: 'br' },
      { name: 'Brunei', code: 'bn' },
      { name: 'Bulgaria', code: 'bg' }
    ],
    C: [
      { name: 'Cambodia', code: 'kh' },
      { name: 'Cameroon', code: 'cm' },
      { name: 'Canada', code: 'ca' },
      { name: 'Chad', code: 'td' },
      { name: 'Chile', code: 'cl' },
      { name: 'China', code: 'cn' },
      { name: 'Colombia', code: 'co' },
      { name: 'Congo (DRC)', code: 'cd' },
      { name: 'Costa Rica', code: 'cr' },
      { name: 'Croatia', code: 'hr' },
      { name: 'Cyprus', code: 'cy' },
      { name: 'Czech Republic', code: 'cz' }
    ],
    D: [
      { name: 'Denmark', code: 'dk' },
      { name: 'Dominican Republic', code: 'do' }
    ],
    E: [
      { name: 'Ecuador', code: 'ec' },
      { name: 'Egypt', code: 'eg' },
      { name: 'El Salvador', code: 'sv' },
      { name: 'Equatorial Guinea', code: 'gq' },
      { name: 'Estonia', code: 'ee' },
      { name: 'Ethiopia', code: 'et' }
    ],
    F: [
      { name: 'Finland', code: 'fi' },
      { name: 'France', code: 'fr' }
    ],
    G: [
      { name: 'Gabon', code: 'ga' },
      { name: 'Georgia', code: 'ge' },
      { name: 'Germany', code: 'de' },
      { name: 'Ghana', code: 'gh' },
      { name: 'Greece', code: 'gr' },
      { name: 'Guatemala', code: 'gt' },
      { name: 'Guyana', code: 'gy' }
    ],
    H: [
      { name: 'Honduras', code: 'hn' },
      { name: 'Hungary', code: 'hu' }
    ],
    I: [
      { name: 'Iceland', code: 'is' },
      { name: 'India', code: 'in' },
      { name: 'Indonesia', code: 'id' },
      { name: 'Iran', code: 'ir' },
      { name: 'Iraq', code: 'iq' },
      { name: 'Ireland', code: 'ie' },
      { name: 'Israel', code: 'il' },
      { name: 'Italy', code: 'it' }
    ],
    J: [
      { name: 'Jamaica', code: 'jm' },
      { name: 'Japan', code: 'jp' },
      { name: 'Jordan', code: 'jo' }
    ],
    K: [
      { name: 'Kazakhstan', code: 'kz' },
      { name: 'Kenya', code: 'ke' },
      { name: 'Kuwait', code: 'kw' },
      { name: 'Kyrgyzstan', code: 'kg' }
    ],
    L: [
      { name: 'Latvia', code: 'lv' },
      { name: 'Lebanon', code: 'lb' },
      { name: 'Libya', code: 'ly' },
      { name: 'Lithuania', code: 'lt' },
      { name: 'Luxembourg', code: 'lu' }
    ],
    M: [
      { name: 'Malaysia', code: 'my' },
      { name: 'Malta', code: 'mt' },
      { name: 'Mauritania', code: 'mr' },
      { name: 'Mexico', code: 'mx' },
      { name: 'Moldova', code: 'md' },
      { name: 'Mongolia', code: 'mn' },
      { name: 'Montenegro', code: 'me' },
      { name: 'Morocco', code: 'ma' },
      { name: 'Mozambique', code: 'mz' },
      { name: 'Myanmar', code: 'mm' }
    ],
    N: [
      { name: 'Namibia', code: 'na' },
      { name: 'Netherlands', code: 'nl' },
      { name: 'New Zealand', code: 'nz' },
      { name: 'Nicaragua', code: 'ni' },
      { name: 'Niger', code: 'ne' },
      { name: 'Nigeria', code: 'ng' },
      { name: 'North Macedonia', code: 'mk' },
      { name: 'Norway', code: 'no' }
    ],
    O: [
      { name: 'Oman', code: 'om' }
    ],
    P: [
      { name: 'Pakistan', code: 'pk' },
      { name: 'Panama', code: 'pa' },
      { name: 'Papua New Guinea', code: 'pg' },
      { name: 'Paraguay', code: 'py' },
      { name: 'Peru', code: 'pe' },
      { name: 'Philippines', code: 'ph' },
      { name: 'Poland', code: 'pl' },
      { name: 'Portugal', code: 'pt' }
    ],
    Q: [
      { name: 'Qatar', code: 'qa' }
    ],
    R: [
      { name: 'Romania', code: 'ro' },
      { name: 'Russia', code: 'ru' },
      { name: 'Rwanda', code: 'rw' }
    ],
    S: [
      { name: 'Saudi Arabia', code: 'sa' },
      { name: 'Senegal', code: 'sn' },
      { name: 'Serbia', code: 'rs' },
      { name: 'Singapore', code: 'sg' },
      { name: 'Slovakia', code: 'sk' },
      { name: 'Slovenia', code: 'si' },
      { name: 'South Africa', code: 'za' },
      { name: 'South Korea', code: 'kr' },
      { name: 'Spain', code: 'es' },
      { name: 'Sri Lanka', code: 'lk' },
      { name: 'Sudan', code: 'sd' },
      { name: 'Suriname', code: 'sr' },
      { name: 'Sweden', code: 'se' },
      { name: 'Switzerland', code: 'ch' },
      { name: 'Syria', code: 'sy' }
    ],
    T: [
      { name: 'Taiwan', code: 'tw' },
      { name: 'Tajikistan', code: 'tj' },
      { name: 'Tanzania', code: 'tz' },
      { name: 'Thailand', code: 'th' },
      { name: 'Trinidad and Tobago', code: 'tt' },
      { name: 'Tunisia', code: 'tn' },
      { name: 'Turkey', code: 'tr' },
      { name: 'Turkmenistan', code: 'tm' }
    ],
    U: [
      { name: 'Uganda', code: 'ug' },
      { name: 'Ukraine', code: 'ua' },
      { name: 'United Arab Emirates', code: 'ae' },
      { name: 'United Kingdom', code: 'gb' },
      { name: 'United States', code: 'us' },
      { name: 'Uruguay', code: 'uy' },
      { name: 'Uzbekistan', code: 'uz' }
    ],
    V: [
      { name: 'Venezuela', code: 've' },
      { name: 'Vietnam', code: 'vn' }
    ],
    W: [],
    X: [],
    Y: [
      { name: 'Yemen', code: 'ye' }
    ],
    Z: [
      { name: 'Zambia', code: 'zm' },
      { name: 'Zimbabwe', code: 'zw' }
    ]
  };

  const alphabet = ['A-Z', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

  const scrollToSection = (letter) => {
    setActiveFilter(letter);
    if (letter === 'A-Z') {
      window.scrollTo({ top: document.getElementById('countries-section').offsetTop - 100, behavior: 'smooth' });
    } else if (sectionRefs.current[letter]) {
      sectionRefs.current[letter].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getFlagUrl = (code) => `https://flagcdn.com/w40/${code}.png`;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 whitespace-nowrap">Country & Regional Intelligence</h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              Comprehensive energy intelligence across global markets delivering analytical depth and strategic insight for informed decision-making.
            </p>
          </div>
        </div>
      </section>

      {/* World Section - Matching Region Card Structure */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg border-2 border-slate-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300 overflow-hidden group">
            <div className="grid lg:grid-cols-2">
              {/* World Map Image */}
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 p-8 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_3d67b805-10ac-45e8-9159-aeffcf377b81/artifacts/wdxklvr6_world%20map.jpg"
                  alt="World Map with Country Flags"
                  className="w-full h-auto max-h-[300px] object-contain rounded-lg"
                />
              </div>
              {/* World Content */}
              <div className="p-8 flex flex-col justify-center">
                <div className="text-blue-700 mb-4">
                  <Globe2 className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">World</h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Global energy intelligence aggregating cross-regional analysis, commodity flows, and macro-level market dynamics. Access comprehensive data spanning all major energy producing and consuming regions worldwide.
                </p>
                <Link
                  to="#regions"
                  className="inline-flex items-center text-blue-700 font-semibold hover:text-blue-900 transition-colors"
                >
                  Explore Global Coverage
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regions Section */}
      <section id="regions" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-4">Regions</h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-3xl mx-auto">
              Explore energy intelligence by geographic region
            </p>
          </div>

          {/* Region Cards Grid - 4 columns on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {regions.map((region, index) => (
              <Link
                key={index}
                to={`#${region.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-white rounded-lg border border-slate-200 hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 overflow-hidden group"
              >
                {/* Region Image */}
                <div className="bg-gradient-to-br from-blue-50 to-slate-50 p-6 border-b border-slate-200">
                  <img 
                    src={region.image}
                    alt={`${region.name} Region Map`}
                    className="w-full h-40 object-contain group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                {/* Region Info */}
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-[#0F2A4D] mb-2 group-hover:text-[#007BFF] transition-colors">
                    {region.name}
                  </h3>
                  <p className="text-base text-slate-600 leading-relaxed mb-4">
                    {region.description}
                  </p>
                  <span className="inline-flex items-center text-sm text-[#007BFF] font-medium group-hover:translate-x-1 transition-transform">
                    Explore region
                    <ArrowRight className="ml-1 w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Countries Section - A-Z Listing */}
      <section id="countries-section" className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-4">Countries</h2>
            <p className="text-base lg:text-lg text-slate-600">
              Browse all countries covered by Enerlligence intelligence
            </p>
          </div>

          {/* Alphabet Navigation Bar */}
          <div className="bg-white border border-slate-200 rounded-lg p-4 mb-8 sticky top-20 z-10 shadow-sm">
            <div className="flex flex-wrap gap-1 justify-center">
              {alphabet.map((letter) => (
                <button
                  key={letter}
                  onClick={() => scrollToSection(letter)}
                  className={`px-3 py-2 text-sm font-medium rounded transition-all duration-200
                    ${activeFilter === letter 
                      ? 'bg-blue-700 text-white' 
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }
                    ${letter === 'A-Z' ? 'font-bold mr-2' : ''}
                  `}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>

          {/* Country List by Letter */}
          <div className="space-y-12">
            {Object.entries(countriesByLetter).map(([letter, countries]) => {
              if (countries.length === 0) return null;
              
              return (
                <div 
                  key={letter} 
                  ref={(el) => (sectionRefs.current[letter] = el)}
                  className="scroll-mt-40 pb-4"
                >
                  {/* Letter Heading */}
                  <div className="flex items-center mb-5">
                    <div className="w-10 h-10 bg-slate-900 text-white rounded-lg flex items-center justify-center text-lg font-bold mr-4">
                      {letter}
                    </div>
                    <div className="flex-1 h-px bg-slate-200"></div>
                  </div>

                  {/* Countries Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {countries.map((country, idx) => (
                      <a
                        key={idx}
                        href={`#country-${country.code}`}
                        className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-blue-400 hover:shadow-md transition-all duration-200 group"
                      >
                        <img 
                          src={getFlagUrl(country.code)}
                          alt={`${country.name} flag`}
                          className="w-8 h-6 object-cover rounded shadow-sm flex-shrink-0"
                          loading="lazy"
                        />
                        <span className="text-slate-700 group-hover:text-blue-700 font-medium text-sm truncate">
                          {country.name}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section - Matching Commodities */}
      <section className="py-20 bg-gradient-to-r from-[#157D3F] via-[#195C63] to-[#1D3B87]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need Country-Specific Intelligence?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Our regional analysts provide customized country intelligence tailored to your strategic requirements
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#1D3B87] font-semibold rounded-md hover:bg-slate-100 transition-all duration-200 shadow-lg"
          >
            Request Country Brief
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Countries;
